import { BBABalance } from 'components/blockchain'
import { TxInterface } from 'interfaces/tx.interface'
import { useCallback, useState } from 'react'
import { FaAngleDown, FaAngleUp } from 'react-icons/fa6'
import { Link } from 'react-router-dom'
import { capitalCase } from 'text-case'

type TableHeaderProps = {
	name: string
	uid: 'address' | 'change' | 'postBalance' | 'detail'
}

type TableDataProps = {
	address: string
	change: number
	postBalance: number
	detail: string[]
}

const tableHeaderData: TableHeaderProps[] = [
	{
		name: 'Address',
		uid: 'address'
	},
	{
		name: 'Change (BBA)',
		uid: 'change'
	},
	{
		name: 'Post Balance (BBA)',
		uid: 'postBalance'
	},
	{
		name: 'Detail',
		uid: 'detail'
	}
]

const transformTxToInputTableData = (tx: TxInterface): TableDataProps[] => {
	return tx.transaction.message.accountKeys.map((account, index) => {
		const balanceChange = tx.meta.postBalances[index] - tx.meta.preBalances[index]

		const details: string[] = []
		if (index === 0) details.push('fee payer')
		if (account.writable) details.push('writable')
		if (account.signer) details.push('signer')
		if (tx.transaction.message.instructions.some((ix) => ix.programId === account.pubkey)) {
			details.push('program')
		}

		return {
			address: account.pubkey,
			change: balanceChange,
			postBalance: tx.meta.postBalances[index],
			detail: [...new Set(details)]
		}
	})
}

export default function InputDetail({ data }: { data: TxInterface }) {
	const [inputVisible, setInputVisible] = useState<boolean>(true)
	const transformedData = transformTxToInputTableData(data)

	const renderCell = useCallback((header: TableHeaderProps, row: TableDataProps) => {
		switch (header.uid) {
			case 'address':
				return (
					<Link to={`/address/${row.address}`} className="flex max-w-[320px]">
						<div className="truncateWrapper">
							<p className="text-small truncate text-green-900">{row.address}</p>
						</div>
					</Link>
				)
			case 'detail':
				return (
					<div className="flex justify-end space-x-2">
						{row.detail.map((detail, index) => (
							<span key={index} className="badge badge-info">
								{capitalCase(detail)}
							</span>
						))}
					</div>
				)
			default:
				return <BBABalance daltons={row[header.uid]} />
		}
	}, [])

	return (
		<div className="box mb-4 overflow-hidden bg-white px-2.5 py-5 dark:bg-darkPrimary md:px-5">
			<div className="flex justify-between border-b-[1px] border-gray-100 py-4 dark:border-lightGrey">
				<h2 className="text-large">Input(s)</h2>
				<button
					type="button"
					className="text-small flex items-center text-green-900"
					onClick={() => setInputVisible(!inputVisible)}
				>
					{`${!inputVisible ? 'View' : 'Hide'} More Details`}
					<span className="ml-[1px] text-xs">{!inputVisible ? <FaAngleDown /> : <FaAngleUp />}</span>
				</button>
			</div>
			<div className={`mt-2 flex flex-col ${inputVisible ? '' : 'hidden'}`}>
				<div className="table-wrapper">
					<table className="table w-full">
						<thead>
							<tr>
								{tableHeaderData.map((header) => (
									<th key={header.uid}>{header.name}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{transformedData.map((row, index) => (
								<tr key={index}>
									{tableHeaderData.map((header) => (
										<td>{renderCell(header, row)}</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}
